import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import AdminSlideDrawer from '@sharedCR/common/AdminSlideDrawer';
import SearchInputCDS from '@sharedCR/coreDesignSystem/SearchInputCDS';
import { tpoCertificationsApi } from 'app/services/api';
import PropTypes from 'prop-types';

import { AiProcessOwnerKind, AiProcessSubjectRequirement } from 'constants/aiProcessRuns';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  selectLiveRunsByOwner,
  selectRunsByOwner,
  startAiProcessExecutionAction,
} from 'store/aiProcessRuns';
import { closeConfirmationDialogAction, openConfirmationDialogAction } from 'store/global/actions';
import { getAiProcessesAction } from 'store/system/actions';
import { selectAiProcesses } from 'store/system/selectors';

import CertificationSubjectStep from './CertificationSubjectStep';
import ProcessPickerCard from './ProcessPickerCard';
import {
  DRAWER_SUBTITLE,
  DRAWER_TITLE,
  DRAWER_WIDTH,
  EMPTY_STATE_MESSAGE,
  LOADING_MESSAGE,
  NO_MATCH_STATE_MESSAGE,
  SEARCH_PLACEHOLDER,
} from './StartAiProcessDrawer.constants';
import {
  drawerContentStyles,
  emptyStateStyles,
  listStyles,
  searchRowStyles,
  subtitleStyles,
} from './StartAiProcessDrawer.styles';
import { StartAiProcessDrawerTestIds } from './StartAiProcessDrawer.testIds';
import {
  filterProcessesBySearch,
  filterStartableProcesses,
  findLastRunForProcess,
  findLiveRunForProcess,
} from './StartAiProcessDrawer.utils';

const StartAiProcessDrawer = ({
  open,
  ownerKind,
  ownerId,
  onClose,
  onStartSuccess,
  onOpenLiveRun,
}) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [startingProcessId, setStartingProcessId] = useState(null);

  // DEV-3049: certification-subject step state (SubCompany-scoped processes only).
  const [subjectProcess, setSubjectProcess] = useState(null);
  const [selectedCertIds, setSelectedCertIds] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [isLoadingCertifications, setIsLoadingCertifications] = useState(false);

  const isSubCompanyOwner = ownerKind === AiProcessOwnerKind.SUB_COMPANY;

  const processes = useSelector(selectAiProcesses);
  const runs = useSelector(selectRunsByOwner(ownerId));
  const liveRuns = useSelector(selectLiveRunsByOwner(ownerId));

  const startableProcesses = useMemo(
    () => filterStartableProcesses(processes, ownerKind),
    [processes, ownerKind],
  );

  const visibleProcesses = useMemo(
    () => filterProcessesBySearch(startableProcesses, searchTerm),
    [startableProcesses, searchTerm],
  );

  const startProcess = useCallback(
    async (process, subjectCertificationIds = null) => {
      setStartingProcessId(process.id);

      try {
        const execution = await dispatch(
          startAiProcessExecutionAction({
            processId: process.id,
            ownerId,
            subjectCertificationIds,
          }),
        );

        if (execution?.id) {
          onStartSuccess(execution.id);
        }
      } finally {
        setStartingProcessId(null);
      }
    },
    [dispatch, ownerId, onStartSuccess],
  );

  const onStart = useCallback(
    process => {
      // SubCompany-scoped processes that declare a subject requirement route
      // through the certification picker instead of the immediate confirm.
      const requiresSubject =
        isSubCompanyOwner &&
        !!process?.subjectRequirement &&
        process.subjectRequirement !== AiProcessSubjectRequirement.NONE;

      if (requiresSubject) {
        setSelectedCertIds([]);
        setSubjectProcess(process);

        return;
      }

      const liveRun = findLiveRunForProcess(liveRuns, process.id);
      const isAlreadyRunning = !!liveRun;

      const message = isAlreadyRunning
        ? 'A run is already in progress — start anyway?'
        : `Start ${process.name}?`;

      const description = isAlreadyRunning
        ? `${process.name} is currently running. Starting another run won't stop the one already in progress.`
        : 'A new run will begin and you can follow along live.';

      dispatch(
        openConfirmationDialogAction({
          message,
          description,
          onAcceptButtonName: 'Start',
          onAccept: () => {
            dispatch(closeConfirmationDialogAction());
            startProcess(process);
          },
        }),
      );
    },
    [dispatch, liveRuns, startProcess, isSubCompanyOwner],
  );

  const onToggleCert = useCallback(certId => {
    setSelectedCertIds(prev =>
      prev.includes(certId) ? prev.filter(id => id !== certId) : [...prev, certId],
    );
  }, []);

  const onBackFromSubject = useCallback(() => {
    setSubjectProcess(null);
    setSelectedCertIds([]);
  }, []);

  const onStartWithSubjects = useCallback(
    certIds => {
      if (subjectProcess) {
        startProcess(subjectProcess, certIds);
      }
    },
    [subjectProcess, startProcess],
  );

  const onSearch = useCallback(value => {
    setSearchTerm(value);
  }, []);

  const emptyMessage = useMemo(() => {
    if (startableProcesses.length === 0) {
      return EMPTY_STATE_MESSAGE;
    }

    return NO_MATCH_STATE_MESSAGE;
  }, [startableProcesses.length]);

  useEffectSafeStrictMode(() => {
    if (!open) {
      setSearchTerm('');
      setStartingProcessId(null);
      setSubjectProcess(null);
      setSelectedCertIds([]);

      return;
    }

    setIsLoading(true);
    dispatch(getAiProcessesAction()).finally(() => setIsLoading(false));
  }, [open, dispatch]);

  useEffectSafeStrictMode(() => {
    if (!open || !isSubCompanyOwner || !ownerId) {
      return;
    }

    setIsLoadingCertifications(true);
    tpoCertificationsApi
      .getHistory({ subCompanyId: ownerId })
      .then(response => setCertifications(response?.data?.all ?? []))
      .catch(() => setCertifications([]))
      .finally(() => setIsLoadingCertifications(false));
  }, [open, isSubCompanyOwner, ownerId]);

  const content = subjectProcess ? (
    <CertificationSubjectStep
      certifications={certifications}
      isLoading={isLoadingCertifications}
      isStarting={startingProcessId === subjectProcess.id}
      process={subjectProcess}
      requirement={subjectProcess.subjectRequirement}
      selectedCertIds={selectedCertIds}
      onBack={onBackFromSubject}
      onStart={onStartWithSubjects}
      onToggleCert={onToggleCert}
    />
  ) : (
    <Box data-testid={StartAiProcessDrawerTestIds.CONTAINER} sx={drawerContentStyles}>
      <Typography sx={subtitleStyles} variant="regular14">
        {DRAWER_SUBTITLE}
      </Typography>
      <Box sx={searchRowStyles}>
        <SearchInputCDS
          searchOnType
          ariaLabel="Search AI processes"
          data-testid={StartAiProcessDrawerTestIds.SEARCH_INPUT}
          placeholder={SEARCH_PLACEHOLDER}
          onSearch={onSearch}
        />
      </Box>
      {isLoading && (
        <Box data-testid={StartAiProcessDrawerTestIds.LOADING} sx={emptyStateStyles}>
          <Typography variant="regular14">{LOADING_MESSAGE}</Typography>
        </Box>
      )}
      {!isLoading && visibleProcesses.length === 0 && (
        <Box data-testid={StartAiProcessDrawerTestIds.EMPTY_STATE} sx={emptyStateStyles}>
          <Typography variant="regular14">{emptyMessage}</Typography>
        </Box>
      )}
      {!isLoading && visibleProcesses.length > 0 && (
        <Box data-testid={StartAiProcessDrawerTestIds.PROCESS_LIST} sx={listStyles}>
          {visibleProcesses.map(process => (
            <ProcessPickerCard
              isStarting={startingProcessId === process.id}
              key={process.id}
              lastRun={findLastRunForProcess(runs, process.id)}
              liveRun={findLiveRunForProcess(liveRuns, process.id)}
              process={process}
              onOpenLiveRun={onOpenLiveRun}
              onStart={onStart}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <AdminSlideDrawer
      content={content}
      open={open}
      title={DRAWER_TITLE}
      width={DRAWER_WIDTH}
      onClose={onClose}
    />
  );
};

StartAiProcessDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  ownerKind: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenLiveRun: PropTypes.func.isRequired,
  onStartSuccess: PropTypes.func.isRequired,
  ownerId: PropTypes.string,
};

export default memo(StartAiProcessDrawer);
